import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { QuarterFormatEnum } from '@planning/types';
import { CalendarType, PeriodAggregationEnum } from '@unified-ui/shared/types';
import { buildTreeFromHierarchy, checkedParentNodesSelection, enumToSelectItem, flattenTreeNodes, getEnumValueFromString } from '@unified-ui/utilities';
import { groupBy, xorBy } from 'lodash-es';
import { TreeNode } from 'primeng/api';
import { SelectItem } from 'primeng/api/selectitem';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { Tree, TreeModule } from 'primeng/tree';
import { combineLatest, distinct, map, Observable, of, shareReplay, startWith, take, tap, withLatestFrom } from 'rxjs';
import { LoaderComponent } from '../loader/loader.component';
import { ICalendarDataList } from './types/calendar-data-list.types';

interface IPeriodsState<T> {
    customCalendars?: T[];
    selected?: T[] | null;
    selectedAggregationType?: PeriodAggregationEnum | null;
    selectedCalendarType?: string | null;
    selectedQuarterFormat?: string | null;
    standardCalendars?: T[];
}

enum QuarterFormatListEnum {
    Q1 = 'JFM',
    Q2 = 'AMJ',
    Q3 = 'JAS',
    Q4 = 'OND'
}

/**
 * Component for managing and displaying period selections.
 * Supports both standard and custom calendars with aggregation options.
 */
@Component({
    selector: 'shared-periods',
    standalone: true,
    imports: [AsyncPipe, FormsModule, SelectModule, TreeModule, CheckboxModule, LoaderComponent],
    templateUrl: './periods.component.html',
    styleUrls: ['./periods.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeriodsComponent<T> implements OnInit, AfterViewInit {
    /**
     * @description Custom label for building the tree hierarchy
     */
    private _customLabelFunc = (key: string, label: string, quarterFormat?: string) => {
        switch (key) {
            case this.customLabelKey(): {
                return this.customLabelKey() === 'weekNum' ? label : `Week of ${label.split('-').slice(1).join('/')}`;
            }
            case 'quarterName': {
                return quarterFormat === QuarterFormatEnum.Code ? QuarterFormatListEnum[label as keyof typeof QuarterFormatListEnum] : label;
            }
            default: {
                return label;
            }
        }
    };

    /** State for calendarDataOptions load once */
    private _hasLoaded = false;

    // Indicate if we've loaded the data from the calendar types

    /** Calendar tree for displaying options */
    public activeCalendarTree = signal<TreeNode[]>([]);

    public periodAggregationEnum = PeriodAggregationEnum;

    /** List of calendar Data Options */
    public readonly calendarDataOptions$ = input<Observable<T[] | undefined | any>>(of([]));

    /** Emits when the calendar type changes */
    public readonly calendarTypeChange = output<string>();

    /** Custom label for building the tree hierarchy */
    public readonly customLabelKey = input<'startDate' | 'weekStartDate' | 'weekNum'>('startDate');

    /** Emits when the selection deselect all */
    public readonly deselectAllChange = output<void>();
    public destroyRef = inject(DestroyRef);

    /** Emits when the aggregation type changes */
    public readonly granularityChange = output<PeriodAggregationEnum>();

    /** Emits when the quarter format changes */
    public readonly quarterFormatChange = output<QuarterFormatEnum>();

    /** Granularity options */
    public granularityList: SelectItem[] = [];

    /** Quarter Format options */
    public quarterFormatList: SelectItem[] = [];

    /** Loading state for displaying loader */
    public isLoading: boolean = false;

    /** Emits when the period selection changes */
    public readonly periodSelectionChange = output<T[]>();

    /** Current calendar state */
    public readonly periodsState$ = input<Observable<IPeriodsState<T>>>(of());

    /** Currently selected calendar type (e.g., 'standard' or 'custom') */
    public readonly savedCalendarType$ = input<Observable<string | null | undefined> | undefined>(of());

    /** Selected aggregation type */
    public readonly selectedAggregationType$ = input<Observable<PeriodAggregationEnum | null | undefined>>(of());

    /** Selected quarter format */
    public readonly selectedQuarterFormat$ = input<Observable<QuarterFormatEnum | null | undefined>>(of());

    /** Selected Calendar Type */
    public readonly selectedCalendarType$ = input<Observable<ICalendarDataList<T> | undefined>>(of());

    /** Custom label for building the tree hierarchy */
    public readonly yearPropertyCondition = input<boolean>(true);

    /** Indicate if we've shows granularity from the parent component */
    public readonly hideGranularity = input<boolean>(false);

    /** Set if quarter format is shown */
    public readonly enableQuarterFormat = input<boolean>(true);

    /**
     * Set observables via selects (inputs)
     */
    protected calendarDataOptionList$ = computed(() => this._calendarDataOptions());

    protected selectedGranularity$ = computed(() => this._selectedGranularity());

    protected selectedCalendars$ = computed(() => this._selectedCalendars());

    protected selectedQuarter$ = computed(() => this._selectedQuarterFormat());

    private _selectedPeriods = signal<TreeNode | TreeNode[] | null>([]);

    protected isAllSelected = computed(() => {
        const selection: TreeNode[] | null = this._selectedPeriods() as TreeNode[] | null;
        const values = this.activeCalendarTree()
        if (selection?.length === 0) {
            // TriStateCheckbox has 3 states: True, False, null
            // eslint-disable-next-line unicorn/no-null
            return null;
        }

        if (selection) {
            return flattenTreeNodes(values).length - selection.length === 0;
        }

        // Some are selected
        return false;
    })

    /**
     * Builds calendar TreeNodes to the store.
     * @param calendars An array of calendar entries.
     */
    public buildCalendarTree(calendars: (T | any)[], quarterFormat?: QuarterFormatEnum): TreeNode[] {
        let returnNodes: TreeNode[] = [];
        combineLatest([
            this.selectedAggregationType$().pipe(
                map(s => (s ? getEnumValueFromString(PeriodAggregationEnum, s) : undefined)),
                startWith(PeriodAggregationEnum.Month)
            ),
            this.selectedCalendarType$()
        ])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map(([aggregationType, selectedCalendarType]) => {
                    let treeNodes: TreeNode[] = [];
                    let yearProperty;
                    if (this.hideGranularity()) {
                        // Set calendars to selectedCalendarType if calendars is empty
                        calendars = (calendars.length ? calendars : selectedCalendarType?.value) || [];
                    }

                    if (this.yearPropertyCondition()) {
                        yearProperty = selectedCalendarType?.key === CalendarType.Standard ? 'year' : 'programSeason';
                    } else {
                        yearProperty = 'year';
                    }
                    switch (aggregationType) {
                        case PeriodAggregationEnum.Year: {
                            const grouped = groupBy(calendars, calendar => calendar.year);
                            const flatData = Object.values(grouped).map(group => group[0]);
                            treeNodes = buildTreeFromHierarchy(flatData, [yearProperty as keyof T]);
                            break;
                        }
                        case PeriodAggregationEnum.Quarter: {
                            const grouped = groupBy(calendars, calendar => `${calendar.year}|${calendar.quarter}`);
                            const flatData = Object.values(grouped).map(group => group[0]);
                            treeNodes = buildTreeFromHierarchy(flatData, [yearProperty as keyof T, 'quarterName' as keyof T], this._customLabelFunc, quarterFormat);
                            break;
                        }
                        case PeriodAggregationEnum.Month: {
                            const grouped = groupBy(calendars, calendar => `${calendar.year}|${calendar.month}`);
                            const flatData = Object.values(grouped).map(group => group[0]);

                            treeNodes = buildTreeFromHierarchy(flatData, [yearProperty as keyof T, 'monthName' as keyof T]);
                            break;
                        }
                        case PeriodAggregationEnum.Week:
                        case PeriodAggregationEnum.Day: {
                            const grouped = groupBy(calendars, calendar => `${calendar.year}|${calendar[this.customLabelKey()]}`);
                            const flatData = Object.values(grouped).map(group => group[0]);

                            treeNodes = buildTreeFromHierarchy(flatData, [yearProperty as keyof T, this.customLabelKey() as keyof T], this._customLabelFunc);
                            break;
                        }
                    }

                    return treeNodes;
                })
            )
            .subscribe(nodes => (returnNodes = nodes));

        return returnNodes;
    }

    /**
     * Returns granularity options to omit based on the calendar type.
     * @param {string} calendarType - The calendar type.
     * @returns {string[]} Granularity options to omit.
     */
    public granularityOptionsToOmit(calendarType: string): string[] {
        return [];
    }

    public ngAfterViewInit(): void {
        this.granularityList = enumToSelectItem(PeriodAggregationEnum);
        this.quarterFormatList = [
            { label: 'Numbered (ex: Q1)', value: QuarterFormatEnum.Number },
            { label: 'Code (ex: JFM)', value: QuarterFormatEnum.Code }
        ];

        // We may have selections in the store. We ensure the activeCalendarTree will be populated.
        combineLatest([this.savedCalendarType$(), this.calendarDataOptions$(), this.selectedQuarter$()])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(([selectedType, calendarTypeData, selectedQuarterFormat]: any[]) => {
                    selectedType && this.setGranularityOptions(selectedType);
                    const data = (calendarTypeData as ICalendarDataList<T>[]).find((option: ICalendarDataList<T>) => option.key === (selectedType as string));
                    if (data) {
                        this.activeCalendarTree.set(this.buildCalendarTree(data?.value, selectedQuarterFormat));
                        this.setPartialSelected();
                    }
                })
            )
            .subscribe();
    }

    public ngOnInit(): void {
        // When hideGranularity is true set activeCalendarTree
        if (this.hideGranularity()) this.activeCalendarTree.set(this.buildCalendarTree([]));
    }

    // limits the granularity options according to which calendar type is chosen
    public setGranularityOptions(calendarType: string) {
        const omitItems = this.granularityOptionsToOmit(calendarType);
        this.granularityList = enumToSelectItem(PeriodAggregationEnum).filter(item => !omitItems.includes(item.value.toLowerCase()));
    }

    /**
     * Sets the partial selected state for the tree nodes.
     *  - We should encapsulate this in the tree-utils. Currently it works for this case where we only have parent->parent.children
     *  so we'll need to modify it for other cases.
     */
    public setPartialSelected(): void {
        this.selectedCalendars$()
            ?.pipe(
                take(1),
                tap(selectedCalendars => {
                    const flatParentNodes = flattenTreeNodes(this.activeCalendarTree()).filter(f => !f.parent && !f.leaf);
                    const strippedOfParentRecord = selectedCalendars.filter(f => f.leaf);
                    const isNodeSelected = (node: TreeNode): boolean => {
                        return strippedOfParentRecord.some(s => s.key === node.key);
                    };

                    for (const parent of flatParentNodes) {
                        const allSelected = parent.children?.every(child => isNodeSelected(child));
                        const someSelected = parent.children?.some(child => isNodeSelected(child));

                        if (allSelected) {
                            parent.partialSelected = !allSelected;
                        } else if (someSelected) {
                            parent.partialSelected = someSelected;
                        } else {
                            parent.partialSelected = false;
                        }
                    }
                })
            )
            .subscribe();
    }

    /**
     *  Changes aggregation granularity.
     *   - Dispatches the new aggregation type to the store.
     *   - Rebuilds the tree with the new granularity.
     * @param aggType
     */
    protected changeGranularity(aggType: PeriodAggregationEnum): void {
        this.granularityChange.emit(aggType);
        // Changed from Quarter-only to non-Year logic to support quarter formatting for Month and Quarter granularities
        // This ensures quarter format is set for all period types except Year, aligning with UI visibility changes
        if (aggType !== PeriodAggregationEnum.Year && this.enableQuarterFormat()) {
            this.changeQuarterFormat(QuarterFormatEnum.Number);
        } else {
            this.selectedCalendarType$()
                .pipe(
                    take(1),
                    takeUntilDestroyed(this.destroyRef),
                    map(selectedCalendarType => this.buildCalendarTree(selectedCalendarType?.value || []))
                )
                .subscribe(tree => (this.activeCalendarTree.set(tree)));
        }
    }

    /**
     *  Changes quarter format.
     *   - Dispatches the new quarter format to the store.
     *   - Rebuilds the tree with the new quarter format.
     * @param aggType
     */
    protected changeQuarterFormat(quarterFormat: QuarterFormatEnum): void {
        this.quarterFormatChange.emit(quarterFormat);
        this.selectedCalendarType$()
            .pipe(
                take(1),
                takeUntilDestroyed(this.destroyRef),
                map(selectedCalendarType => this.buildCalendarTree(selectedCalendarType?.value || [], quarterFormat))
            )
            .subscribe(tree => (this.activeCalendarTree.set(tree)));
    }

    /**
     * @description Selects or deselect all calendars.
     * @param tree The tree component reference.
     * @param selectAll Determines if all selections are selected.
     */
    protected selectAll(tree: Tree, selectAll: boolean): void {
        if (selectAll) {
            this.setCalendarsSelection(tree?.value);
        } else {
            // TODO: The 'emit' function requires a mandatory void argument
            this.deselectAllChange.emit();
        }
        this.setPartialSelected();
    }

    /**
     * @description Selects all calendars.
     * @param tree The tree component reference.
     */
    protected selectInverse(tree: Tree): void {
        const flattenedMasterNodes = flattenTreeNodes(tree?.value);
        const inverseSelections = xorBy(flattenedMasterNodes, tree?.selection, 'key');
        this.setCalendarsSelection(inverseSelections);
    }

    /**
     * Sets the selected calendar from the user selection
     * - Dispatches the new calendar type selection to the store.
     * - Rebuilds the tree with the new data
     * @param calendarType The calendar type to be dispatched.
     */
    protected setCalendarType(calendarType: ICalendarDataList<T>) {
        this._hasLoaded = false; // Indicate we need to switch data
        this.calendarTypeChange.emit(calendarType.key);
        this.activeCalendarTree.set(this.buildCalendarTree(calendarType.value)) // rebuild the tree w/ new data
    }

    /**
     * @description Saves the selected calendars to the store.
     * @param treeNodes The tree nodes to be saved.
     */
    protected setCalendarsSelection(treeNodes: TreeNode<T[]>[]): void {
        // Flatten the nodes, filter out any parents and get the calendar entries
        const flatNodes: T[] = [];

        flattenTreeNodes(treeNodes)
            .filter(node => node.leaf && node.parent)
            .map(node => flatNodes.push(...node.data));

        if (flatNodes.length === 0) {
            flattenTreeNodes(treeNodes).map(node => flatNodes.push(...node.data));
        }

        this._extractCalendarSelections(flatNodes)
            .pipe(
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(res => {
                this.periodSelectionChange.emit(res);
            });
    }

    protected treeTrackBy(index: number, element: TreeNode): string | undefined {
        return element.key;
    }

    private _calendarDataOptions(): Observable<ICalendarDataList<T>[]> {
        return this.calendarDataOptions$()?.pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() => {
                this.isLoading = false;
                this._hasLoaded = true;
            }),
            shareReplay(1)
        );
    }

    /**
     * @description Matches the selections to the master data and extracts all the calendar items associated with the selections.
     *  - Used to gather the selected calendars.
     *  - Should only be used to find all the Calendar entries for a selection
     * @param calendars T[]
     * @returns calendar selections T []
     */
    private _extractCalendarSelections(calendars: (T | any)[]): Observable<T[]> {
        return combineLatest([this.selectedAggregationType$().pipe(startWith(PeriodAggregationEnum.Month)), this.selectedCalendarType$().pipe(map(selected => selected?.value))]).pipe(
            map(([granularity, selectedCalendarType]) => {
                if (!selectedCalendarType) return [];
                let foundCalendarEntries: T[] = [];
                switch (granularity) {
                    case PeriodAggregationEnum.Year: {
                        for (const cal of calendars) {
                            foundCalendarEntries.push(...selectedCalendarType?.filter((f: T | any) => f.year === cal.year));
                        }
                        break;
                    }
                    case PeriodAggregationEnum.Quarter: {
                        for (const cal of calendars) {
                            foundCalendarEntries.push(...selectedCalendarType?.filter((f: T | any) => f.year === cal.year && f.quarter === cal.quarter));
                        }
                        break;
                    }
                    case PeriodAggregationEnum.Month: {
                        for (const cal of calendars) {
                            foundCalendarEntries.push(...selectedCalendarType?.filter((f: T | any) => f.year === cal.year && f.month === cal.month));
                        }
                        break;
                    }
                    case PeriodAggregationEnum.Week:
                    case PeriodAggregationEnum.Day: {
                        for (const cal of calendars) {
                            foundCalendarEntries.push(...selectedCalendarType?.filter((f: T | any) => f.year === cal.year && f[this.customLabelKey()] === cal[this.customLabelKey()]));
                        }
                        break;
                    }
                }
                return foundCalendarEntries;
            })
        );
    }

    private _selectedCalendars(): Observable<TreeNode[]> {
        return this.periodsState$()?.pipe(
            takeUntilDestroyed(this.destroyRef),
            map(state => {
                const selectedNodes: TreeNode[] = [];
                // Check if we have everything we need
                if (state.selected && state.selected?.length > 0 && ((state.selectedAggregationType && state.selectedCalendarType) || this.hideGranularity())) {
                    // All the data we need should be in the children after we call buildTree
                    // We have to modify our logic a bit here if the granularity is year.
                    if (state.selectedAggregationType === PeriodAggregationEnum.Year) {
                        this.buildCalendarTree(state.selected).map(node => selectedNodes.push(node));
                    } else {
                        // this._buildCalendarTree(state.selected).map(node => node.children ? selectedNodes.push(...node.children) : undefined);
                        this.buildCalendarTree(state.selected, state.selectedQuarterFormat as QuarterFormatEnum).map(node => {
                            if (node.children && node.children.length > 0) {
                                selectedNodes.push(...node.children);
                            }
                        });
                    }

                    const tempNodes = selectedNodes;
                    for (const node of tempNodes) {
                        if (node.parent) checkedParentNodesSelection(selectedNodes, node.parent, true);
                    }
                }

                this._selectedPeriods.set(selectedNodes)
                return selectedNodes;
            }),
            distinct(),
            shareReplay(1)
        );
    }

    private _selectedGranularity(): Observable<PeriodAggregationEnum | undefined> {
        return this.selectedAggregationType$().pipe(
            takeUntilDestroyed(this.destroyRef),
            map(s => (s ? getEnumValueFromString(PeriodAggregationEnum, s) : undefined))
        );
    }

    private _selectedQuarterFormat(): Observable<QuarterFormatEnum | undefined> {
        return this.selectedQuarterFormat$().pipe(
            withLatestFrom(this.selectedGranularity$()),
            takeUntilDestroyed(this.destroyRef),
            map(([s, g]) =>
                s ? getEnumValueFromString(QuarterFormatEnum, s)
                // if a new report set undefined
                // if not set QuarterFormatEnum.Number
                : g ? QuarterFormatEnum.Number
                : undefined
            )
        );
    }
}
